import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { DeviceInfo, DeviceKind } from '@/shared/lib/livekit'

// Конкретное устройство между звонками не запоминается: сохранённый `deviceId`
// уводит захват в железо, которого сегодня может не быть (гарнитуру вынули,
// человек за другим столом), и это тихий отказ — селект показывает одно, звук
// идёт из другого. `undefined` = системное по умолчанию: оно следует за
// системой и потому одинаково работает у всех на старте. Выбор остаётся, но
// живёт в памяти вкладки; персистентны только тумблеры — это привычка
// человека, а не железо.
interface DeviceSelectionState {
  camId: string | undefined
  micId: string | undefined
  outputId: string | undefined
  camEnabled: boolean
  micEnabled: boolean
  setCamId: (id: string | undefined) => void
  setMicId: (id: string | undefined) => void
  setOutputId: (id: string | undefined) => void
  setCamEnabled: (enabled: boolean) => void
  setMicEnabled: (enabled: boolean) => void
  // Устройство исчезло из системы (гарнитуру вынули прямо в звонке) — выбор
  // возвращается на системное, иначе он указывает в пустоту.
  keepPresent: (devices: DeviceInfo[]) => void
}

type PersistedSelection = Pick<DeviceSelectionState, 'camEnabled' | 'micEnabled'>

function stillPresent(
  devices: DeviceInfo[],
  kind: DeviceKind,
  id: string | undefined,
): string | undefined {
  if (id === undefined) return undefined
  const ofKind = devices.filter((d) => d.kind === kind)
  // Пустой список — не доказательство пропажи: без выданного доступа браузер
  // отдаёт перечисление усечённым, и сброс здесь терял бы выбор на ровном месте.
  if (ofKind.length === 0) return id
  return ofKind.some((d) => d.deviceId === id) ? id : undefined
}

export const useDeviceSelection = create<DeviceSelectionState>()(
  persist<DeviceSelectionState, [], [], PersistedSelection>(
    (set) => ({
      camId: undefined,
      micId: undefined,
      outputId: undefined,
      camEnabled: true,
      micEnabled: true,
      setCamId: (camId) => set({ camId }),
      setMicId: (micId) => set({ micId }),
      setOutputId: (outputId) => set({ outputId }),
      setCamEnabled: (camEnabled) => set({ camEnabled }),
      setMicEnabled: (micEnabled) => set({ micEnabled }),
      keepPresent: (devices) =>
        set((s) => ({
          camId: stillPresent(devices, 'videoinput', s.camId),
          micId: stillPresent(devices, 'audioinput', s.micId),
          outputId: stillPresent(devices, 'audiooutput', s.outputId),
        })),
    }),
    {
      name: 'telemost.devices',
      // Версия 1 хранила ещё и deviceId — они отбрасываются вместе с ней,
      // включая уже сохранённые в браузерах.
      version: 2,
      partialize: (s) => ({ camEnabled: s.camEnabled, micEnabled: s.micEnabled }),
      migrate: (persisted) => {
        const legacy = persisted as Partial<PersistedSelection> | undefined
        return { camEnabled: legacy?.camEnabled ?? true, micEnabled: legacy?.micEnabled ?? true }
      },
    },
  ),
)
